import { FC, useState } from 'react'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import { useScoreGrid } from '../../../../graphql/score-grid/hooks/useScoreGrid'
import { FullMeetingFragment, MeetingStepStatus, MeetingStepViewId, ScoreGridDto, Country } from '../../../../types'
import { AppraisalContextType } from '../../AppraisalTypes'
import '../../evaluation/evaluation.less'
import EvaluationContentContainer from '../../evaluation/EvaluationContentContainer'
import { EvaluationFooterComponent } from '../../evaluation/EvaluationFooterComponent'
import { EvaluationStepButtonsComponent } from '../../evaluation/EvaluationStepButtonsComponent'
import { AppraisalStepBtns } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { generateInitialState, allScoreGoalsExist } from '../../evaluation/EvaluationUtil'

interface Props extends AppraisalStepBtns {
  meeting: FullMeetingFragment
}

interface EmployeeMeetingStepEvaluationContentProps extends Props {
  currentGrid: ScoreGridDto
  isIdVWeightEnabled: boolean
}

const EmployeeMeetingStepEvaluation: FC<Props> = ({ meeting, backBtn, nextBtn }) => {
  const { data, loading, error } = useScoreGrid(meeting.id, meeting.employee.id, meeting.appraisalSeason.country as Country, true)
  if (loading || error || !data || !data.scoreGridWithHistory) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data || !data.scoreGridWithHistory} error={error} />
  }

  const currentGrid = { ...data.scoreGridWithHistory }
  const isIdVWeightEnabled = data.isIdVWeightEnabled

  return (
    <EmployeeMeetingStepEvaluationContent
      isIdVWeightEnabled={isIdVWeightEnabled}
      currentGrid={currentGrid}
      meeting={meeting}
      backBtn={backBtn}
      nextBtn={nextBtn}
    />
  )
}

const EmployeeMeetingStepEvaluationContent: FC<EmployeeMeetingStepEvaluationContentProps> = ({
  meeting,
  backBtn,
  nextBtn,
  currentGrid,
  isIdVWeightEnabled,
}) => {
  const [evaluationStepStatus, setEvaluationStepStatus] = useState(MeetingStepStatus.Visited)
  const [goalsState, setGoalsState] = useState(generateInitialState(currentGrid.scoreGoals, AppraisalContextType.Employee))
  const showFooter =
    !!currentGrid.lastResultScore ||
    (isIdVWeightEnabled && (!!currentGrid.employeePreparationScore || allScoreGoalsExist(goalsState, AppraisalContextType.Employee)))

  return (
    <>
      <EvaluationContentContainer
        goalsState={goalsState}
        setGoalsState={setGoalsState}
        grid={currentGrid}
        meeting={meeting}
        stepStatus={evaluationStepStatus}
        type={AppraisalContextType.Employee}
        onStatusChange={setEvaluationStepStatus}
        showFooter={showFooter}
      />
      <EvaluationFooterComponent
        meeting={meeting}
        isIdVWeightEnabled={isIdVWeightEnabled}
        currentGrid={currentGrid}
        type={AppraisalContextType.Employee}
        goalsState={goalsState}
        evaluationScore={currentGrid.employeePreparationScore}
        showFooter={showFooter}
      />

      <EvaluationStepButtonsComponent backBtn={backBtn} nextBtn={nextBtn} currentStepStatus={evaluationStepStatus} />
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.Evaluation} stepStatus={evaluationStepStatus} />
    </>
  )
}

export default EmployeeMeetingStepEvaluation
